import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { getServiceOverview } from "../../store/Slices/ServiceSlice";
import { pendingMaintenanceIcon, serviceCompletedIcon, serviceCostIcon, upcomingInspection,
  visualizeChartBlackIcon, visualizeChartWhiteIcon } from "../../utils/svgs";

const ServiceBoxChart = () => {
  const dispatch = useDispatch();
  const [showChart, setShowChart] = useState(false);

  const { serviceOverviewData } = useSelector((state) => state.services);

  useEffect(() => {
    dispatch(getServiceOverview());
  }, []);

  // Prepare chart data
  const serviceCompleteData =
    serviceOverviewData?.CompletedService?.lastThreeMonthsCounts?.map(
      (item) => ({
        month: item.month,
        count: item.count,
      })
    ) || [];

  const serviceCostData =
    serviceOverviewData?.averageService?.lastThreeMonthsAverageCost?.map(
      (item) => ({
        month: item.month,
        count: item.averageCost,
      })
    ) || [];

  const pendingMaintenanceData =
    serviceOverviewData?.pendingService?.lastThreeMonthsCounts?.map((item) => ({
      month: item.month,
      count: item.count,
    })) || [];

  const upcomingMaintenanceData =
    serviceOverviewData?.upComingService?.chart?.map((item) => ({
      month: item.month,
      count: item.count,
    })) || [];

  return (
    <div className="service_box_chart_main">
      <div className="service_box_chart">
        {!showChart ? (
          <span onClick={() => setShowChart(!showChart)}>
            {visualizeChartBlackIcon}
          </span>
        ) : (
          <span onClick={() => setShowChart(!showChart)}>
            {visualizeChartWhiteIcon}
          </span>
        )}
      </div>

      <div>
        <Row>
          <Col md={3}>
            <div className="service_box_chart_item">
              <div className="item_details">
                <div>
                  <h6>Total Services Completed</h6>
                  <div>{serviceOverviewData?.CompletedService?.total || 0}</div>
                  <p>
                    <b>
                      {Math.abs(
                        serviceOverviewData?.CompletedService
                          ?.upFromLastMonth || 0
                      ).toFixed(2)}
                      %
                    </b>{" "}
                    {serviceOverviewData?.CompletedService?.upFromLastMonth < 0
                      ? "down"
                      : "up"}{" "}
                    from Last Month
                  </p>
                </div>
                <div>
                  <span>{serviceCompletedIcon}</span>
                </div>
              </div>
              {showChart && (
                <div style={{ width: "100%", height: 180, marginTop: 30 }}>
                  <ResponsiveContainer>
                    <AreaChart
                      data={serviceCompleteData}
                      margin={{ top: 10, right: 0, left: -25, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id="colorCount"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#43b649"
                            stopOpacity={0.8}
                          />
                          <stop
                            offset="100%"
                            stopColor="#43b649"
                            stopOpacity={0.2}
                          />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="month" tick={{ fontSize: 13 }} />
                      <YAxis tick={{ fontSize: 13 }} />
                      <CartesianGrid strokeDasharray="3 3" />
                      <Tooltip />
                      <Area
                        type="monotone"
                        dataKey="count"
                        stroke="#43b649"
                        fill="url(#colorCount)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </Col>
          <Col md={3}>
            <div className="service_box_chart_item">
              <div className="item_details">
                <div>
                  <h6>Average Service Cost</h6>
                  <div>
                    PKR{" "}
                    {serviceOverviewData?.averageService?.averageServiceCost ||
                      0}
                  </div>
                  <p>
                    <b>
                      {typeof serviceOverviewData?.averageService
                        ?.lastMonthPercentage === "number"
                        ? Math.abs(
                          serviceOverviewData?.averageService
                            ?.lastMonthPercentage
                        ).toFixed(2)
                        : 0}
                      %
                    </b>{" "}
                    {serviceOverviewData?.averageService?.lastMonthPercentage <
                      0
                      ? "down"
                      : "up"}{" "}
                    from Last Month
                  </p>
                </div>
                <div>
                  <span>{serviceCostIcon}</span>
                </div>
              </div>
              {showChart && (
                <div style={{ width: "100%", height: 180, marginTop: 30 }}>
                  <ResponsiveContainer>
                    <AreaChart
                      data={serviceCostData}
                      margin={{ top: 10, right: 0, left: -15, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id="serviceCost"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#FF9500"
                            stopOpacity={0.8}
                          />
                          <stop
                            offset="100%"
                            stopColor="#FF9500"
                            stopOpacity={0.2}
                          />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="month" tick={{ fontSize: 13 }} />
                      <YAxis tick={{ fontSize: 13 }} />
                      <CartesianGrid strokeDasharray="3 3" />
                      <Tooltip />
                      <Area
                        type="monotone"
                        dataKey="count"
                        stroke="#FF9500"
                        fill="url(#serviceCost)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </Col>
          <Col md={3}>
            <div className="service_box_chart_item">
              <div className="item_details">
                <div>
                  <h6>Pending Maintenance</h6>
                  <div>
                    {serviceOverviewData?.pendingService?.total || 0} Vehicles
                  </div>
                  <p>
                    <b>
                      {typeof serviceOverviewData?.pendingService
                        ?.lastMonthPercentage === "number"
                        ? Math.abs(
                          serviceOverviewData?.pendingService
                            ?.lastMonthPercentage
                        ).toFixed(2)
                        : 0}
                      %
                    </b>{" "}
                    {serviceOverviewData?.pendingService?.lastMonthPercentage <
                      0
                      ? "down"
                      : "up"}{" "}
                    from Last Month
                  </p>
                </div>
                <div>
                  <span>{pendingMaintenanceIcon}</span>
                </div>
              </div>
              {showChart && (
                <div style={{ width: "100%", height: 180, marginTop: 30 }}>
                  <ResponsiveContainer>
                    <AreaChart
                      data={pendingMaintenanceData}
                      margin={{ top: 10, right: 0, left: -25, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id="pendingMaintenance"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#F8E34F"
                            stopOpacity={0.8}
                          />
                          <stop
                            offset="100%"
                            stopColor="#F8E34F"
                            stopOpacity={0.2}
                          />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="month" tick={{ fontSize: 13 }} />
                      <YAxis tick={{ fontSize: 13 }} />
                      <CartesianGrid strokeDasharray="3 3" />
                      <Tooltip />
                      <Area
                        type="monotone"
                        dataKey="count"
                        stroke="#F8E34F"
                        fill="url(#pendingMaintenance)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </Col>
          <Col md={3}>
            <div className="service_box_chart_item">
              <div className="item_details">
                <div>
                  <h6>Upcoming Inspections</h6>
                  <div>
                    {serviceOverviewData?.upComingService?.widget
                      ?.currentCount || 0}{" "}
                    in next 30 Days
                  </div>
                  <p>
                    <b>
                      {typeof serviceOverviewData?.upComingService?.widget
                        ?.percentage === "number"
                        ? serviceOverviewData?.upComingService?.widget
                          ?.percentage
                        : 0}
                      %
                    </b>{" "}
                    {serviceOverviewData &&
                      serviceOverviewData?.upComingService?.widget
                        ?.direction}{" "}
                    from Last Month
                  </p>
                </div>
                <div>
                  <span>{upcomingInspection}</span>
                </div>
              </div>
              {showChart && (
                <div style={{ width: "100%", height: 180, marginTop: 30 }}>
                  <ResponsiveContainer>
                    <AreaChart
                      data={upcomingMaintenanceData}
                      margin={{ top: 10, right: 0, left: -25, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id="colorUpcoming"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#4014B8"
                            stopOpacity={0.5}
                          />
                          <stop
                            offset="100%"
                            stopColor="#4014B8"
                            stopOpacity={0.1}
                          />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="month" tick={{ fontSize: 13 }} />
                      <YAxis tick={{ fontSize: 13 }} />
                      <CartesianGrid strokeDasharray="3 3" />
                      <Tooltip />
                      <Area
                        type="monotone"
                        dataKey="count"
                        stroke="#4014B8"
                        fill="url(#colorUpcoming)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ServiceBoxChart;
